import { useEffect, useRef, useState } from 'react';
import { Rows3, Check, ChevronDown } from 'lucide-react';

/**
 * Sprint 12 — sélecteur de densité d'affichage (hauteur des lignes du
 * calendrier). Trois modes communs au Schedule et au Logtime calendaire :
 *  - Compact      : hauteur actuelle.
 *  - Confortable  : plus de hauteur (plus de texte visible dans les blocs).
 *  - Spacieux     : encore plus de hauteur (tâches + notes).
 *
 * Le mode est persisté en localStorage par les vues (clé `synolog_density`),
 * partagé entre les deux écrans.
 */
export const DENSITY_OPTIONS = [
  { key: 'compact', label: 'Compact' },
  { key: 'comfortable', label: 'Confortable' },
  { key: 'spacious', label: 'Spacieux' },
];

/** Hauteur de ligne (px) par mode de densité. */
export const DENSITY_ROW_HEIGHT = {
  compact: 80,
  comfortable: 124,
  spacious: 176,
};

const STORAGE_KEY = 'synolog_density';

/** Lit la densité persistée (fallback `compact`). */
export function readDensity() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && DENSITY_ROW_HEIGHT[v]) return v;
  } catch {
    /* ignore */
  }
  return 'compact';
}

/** Persiste la densité choisie. */
export function writeDensity(v) {
  try {
    window.localStorage.setItem(STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
}

export default function DensityPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = DENSITY_OPTIONS.find((o) => o.key === value) ?? DENSITY_OPTIONS[0];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Densité d'affichage"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          color: 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-pill)',
          padding: '4px 12px',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <Rows3 size={14} /> {current.label} <ChevronDown size={13} />
      </button>
      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 1001,
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: 4,
            minWidth: 180,
          }}
        >
          <div
            style={{
              padding: '4px 10px',
              fontSize: 11,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Densité
          </div>
          {DENSITY_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              role="menuitemradio"
              aria-checked={value === o.key}
              onClick={() => {
                onChange?.(o.key);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '7px 10px',
                background: value === o.key ? 'var(--glass)' : 'transparent',
                color: 'var(--text)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Rows3 size={14} style={{ opacity: 0.7 }} />
              {o.label}
              {value === o.key ? (
                <Check size={13} style={{ marginLeft: 'auto', color: 'var(--accent)' }} />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
